/**
 * Client Service
 * Handles all client management operations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { PROJECT_PRICING, calculateRetainer, getTierForCountry } from '../config/projectPricing';

/**
 * Create a new client
 */
export const createClient = async (userId, clientData) => {
  try {
    // Determine pricing tier based on country
    const countryCode = clientData.address?.country || '';
    const tier = getTierForCountry(countryCode);

    // Calculate retainer if build fee is provided
    const buildFee = clientData.buildFee || 0;
    const monthlyRetainer = buildFee > 0 ? calculateRetainer(buildFee) : 0;

    const client = {
      userId,
      name: clientData.name,
      email: clientData.email,
      phone: clientData.phone || '',
      company: clientData.company || '',
      address: {
        street: clientData.address?.street || '',
        city: clientData.address?.city || '',
        state: clientData.address?.state || '',
        zip: clientData.address?.zip || '',
        country: countryCode,
      },
      defaultRate: clientData.defaultRate || 0,
      currency: clientData.currency || tier.currency,
      notes: clientData.notes || '',
      active: true,
      // Pricing fields (YouTube model: build fee + 20% retainer)
      pricingTier: tier.id,
      complexity: clientData.complexity || 'medium',
      buildFee: buildFee,
      monthlyRetainer: monthlyRetainer,
      // Stripe fields
      stripeCustomerId: clientData.stripeCustomerId || null,
      stripeSubscriptionId: clientData.stripeSubscriptionId || null,
      subscriptionStatus: clientData.subscriptionStatus || null,
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'clients'), client);
    return { clientId: docRef.id, ...client };
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

/**
 * Update an existing client
 */
export const updateClient = async (clientId, clientData) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    const updateData = {
      ...clientData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(clientRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

/**
 * Delete a client
 */
export const deleteClient = async (clientId) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    await deleteDoc(clientRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

/**
 * Get a single client by ID
 */
export const getClient = async (clientId) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    const clientDoc = await getDoc(clientRef);

    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }

    return { id: clientDoc.id, ...clientDoc.data() };
  } catch (error) {
    console.error('Error getting client:', error);
    throw error;
  }
};

/**
 * Get all clients for a user
 */
export const getClients = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'clients'),
      where('userId', '==', userId),
      orderBy('name', 'asc')
    );

    if (filters.active !== undefined) {
      q = query(q, where('active', '==', filters.active));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting clients:', error);
    throw error;
  }
};

/**
 * Subscribe to clients changes
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @param {boolean} isAdmin - If true, returns all clients regardless of userId
 */
export const subscribeToClients = (userId, callback, isAdmin = false) => {
  // Admins can see all clients
  const q = isAdmin
    ? query(collection(db, 'clients'), orderBy('name', 'asc'))
    : query(
        collection(db, 'clients'),
        where('userId', '==', userId),
        orderBy('name', 'asc')
      );

  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(clients);
  });
};

/**
 * Update client pricing (build fee + auto-calculated retainer)
 * @param {string} clientId - Client ID
 * @param {object} pricingData - Pricing data
 * @param {number} pricingData.buildFee - One-time build fee
 * @param {string} pricingData.complexity - 'low' | 'medium' | 'high'
 * @param {string} pricingData.pricingTier - 'PH' | 'GLOBAL'
 */
export const updateClientPricing = async (clientId, pricingData) => {
  try {
    const { buildFee, complexity, pricingTier, currency } = pricingData;
    const monthlyRetainer = buildFee > 0 ? calculateRetainer(buildFee) : 0;

    const updateData = {
      buildFee,
      monthlyRetainer,
      updatedAt: serverTimestamp(),
    };

    if (complexity) updateData.complexity = complexity;
    if (pricingTier) updateData.pricingTier = pricingTier;
    if (currency) updateData.currency = currency;

    const clientRef = doc(db, 'clients', clientId);
    await updateDoc(clientRef, updateData);

    return {
      success: true,
      buildFee,
      monthlyRetainer,
      retainerPercentage: PROJECT_PRICING.RETAINER_PERCENTAGE,
    };
  } catch (error) {
    console.error('Error updating client pricing:', error);
    throw error;
  }
};

/**
 * Update client Stripe subscription details
 * @param {string} clientId - Client ID
 * @param {object} subscriptionData - Stripe subscription data
 */
export const updateClientSubscription = async (clientId, subscriptionData) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    await updateDoc(clientRef, {
      stripeCustomerId: subscriptionData.stripeCustomerId || null,
      stripeSubscriptionId: subscriptionData.stripeSubscriptionId || null,
      subscriptionStatus: subscriptionData.status || null,
      subscriptionCurrentPeriodEnd: subscriptionData.currentPeriodEnd || null,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating client subscription:', error);
    throw error;
  }
};

/**
 * Get clients with active retainer subscriptions
 */
export const getClientsWithRetainers = async (userId) => {
  try {
    const q = query(
      collection(db, 'clients'),
      where('userId', '==', userId),
      where('monthlyRetainer', '>', 0)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting clients with retainers:', error);
    throw error;
  }
};

// ============================================
// Portal Access Management
// ============================================

/**
 * Create or enable portal access for a client
 * @param {string} clientId - Client ID
 * @param {Object} permissions - Optional permissions override
 * @returns {Promise<Object>} Portal access details including URL
 */
export const createPortalAccess = async (clientId, permissions = {}) => {
  try {
    const createPortalAccessFn = httpsCallable(functions, 'createPortalAccessForClient');
    const result = await createPortalAccessFn({ clientId, permissions });
    return result.data;
  } catch (error) {
    console.error('Error creating portal access:', error);
    throw error;
  }
};

/**
 * Get portal access status for a client
 * @param {string} clientId - Client ID
 * @returns {Promise<Object>} Portal access status and details
 */
export const getPortalAccess = async (clientId) => {
  try {
    const getPortalAccessFn = httpsCallable(functions, 'getPortalAccessForClient');
    const result = await getPortalAccessFn({ clientId });
    return result.data;
  } catch (error) {
    console.error('Error getting portal access:', error);
    throw error;
  }
};

/**
 * Disable portal access for a client
 * @param {string} clientId - Client ID
 * @returns {Promise<Object>} Result
 */
export const disablePortalAccess = async (clientId) => {
  try {
    const disablePortalAccessFn = httpsCallable(functions, 'disablePortalAccess');
    const result = await disablePortalAccessFn({ clientId });
    return result.data;
  } catch (error) {
    console.error('Error disabling portal access:', error);
    throw error;
  }
};

/**
 * Regenerate portal access token for a client
 * @param {string} clientId - Client ID
 * @returns {Promise<Object>} New portal access details
 */
export const regeneratePortalToken = async (clientId) => {
  try {
    const regenerateTokenFn = httpsCallable(functions, 'regeneratePortalToken');
    const result = await regenerateTokenFn({ clientId });
    return result.data;
  } catch (error) {
    console.error('Error regenerating portal token:', error);
    throw error;
  }
};

/**
 * Onboard a client in ONE call — the reusable flow that replaces the bespoke
 * setup-<client>.js scripts. Idempotently creates/updates the client, the
 * Stripe customer, a self-service portal link (where the client saves their
 * own card), the plan's subscriptions, an optional GCP usage link, and an
 * optional one-time invoice.
 *
 * @param {Object} args
 * @param {Object} args.client - { company, email, name?, phone?, currency? }
 * @param {string} args.planId - e.g. 'managed-basic' | 'web-managed'
 * @param {Object} [args.params] - { workspaceSeats?, buildFee?, gcpProjectId?, billingAccountId?, invoiceNumber? }
 * @param {Object} [args.permissions] - portal permission overrides
 * @returns {Promise<Object>} { success, clientId, customerId, portalUrl, subscriptionIds, invoiceNumber, planId }
 */
export const onboardClient = async ({ client, planId, params = {}, permissions = {} }) => {
  try {
    const onboardFn = httpsCallable(functions, 'onboardClient');
    const result = await onboardFn({ client, planId, params, permissions });
    return result.data;
  } catch (error) {
    console.error('Error onboarding client:', error);
    throw error;
  }
};
